import { useCallback, useEffect } from 'react';
import { eventBus } from '@atc/cai-event-bus';
import { getObfuscatedCredit } from '@atc/wallet-payment-fns';
import { UDS_CTA_CLICK_REQUEST, UDS_CTA_CLICK_COMPLETED } from 'wallet-manager';

/**
 * Hook to handle external credit verification updates and related event bus events for the Native Deal Calculator.
 *
 * @param {Function} updateUserPaymentInfo - Function to update user payment information
 * @param {Object} financeOffersObj - Object containing finance offer details
 * @param {number} selectedLoanTerm - Currently selected loan term
 * @param {number} loanTerm - Default loan term
 * @param {string} selectedCreditTier - Currently selected credit tier
 * @param {number} financeDownPayment - Finance down payment amount
 * @param {number} offerPrice - User's offer price
 * @param {number} price - Original listing price
 * @returns {{
 *   handleUpdateCreditVerification: Function,
 *   verifyCreditRatingViaEvent: Function
 * }} Object containing handler functions for credit verification
 */
const useExternalSelectedCriteriaUpdate = (updateUserPaymentInfo, financeOffersObj, selectedLoanTerm, loanTerm, selectedCreditTier, financeDownPayment, offerPrice, price) => {
  const handleUpdateCreditVerification = useCallback(function () {
    let {
      verificationError,
      creditScore
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (!verificationError) {
      updateUserPaymentInfo('creditType', getObfuscatedCredit(creditScore), null, true);
    }
  }, [updateUserPaymentInfo]);
  const verifyCreditRatingViaEvent = useCallback((event, clickOrigin) => {
    const {
      payment,
      interestRate: financeInterestRate,
      lender
    } = financeOffersObj || {};
    eventBus.emit(UDS_CTA_CLICK_REQUEST, {
      origin: clickOrigin,
      term: selectedLoanTerm ?? loanTerm,
      creditScore: selectedCreditTier,
      downPayment: financeDownPayment,
      offerPrice: offerPrice ?? price,
      monthlyPayment: payment,
      rate: financeInterestRate,
      lender
    });
  }, [financeOffersObj, selectedLoanTerm, loanTerm, selectedCreditTier, financeDownPayment, offerPrice, price]);
  useEffect(() => {
    eventBus.on(UDS_CTA_CLICK_COMPLETED, handleUpdateCreditVerification);
    return () => {
      eventBus.off(UDS_CTA_CLICK_COMPLETED, handleUpdateCreditVerification);
    };
  }, [handleUpdateCreditVerification]);
  return {
    handleUpdateCreditVerification,
    verifyCreditRatingViaEvent
  };
};
export { useExternalSelectedCriteriaUpdate as default };
